import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useCampaigns } from "../../hooks/useCampaigns";
import { CallType } from "../../service/CallCenterApi";
import {
  CONDITION_OPERATORS,
  createEmptyConditionRow,
  serializeTriggerCondition,
} from "../../components/call-center/triggerCondition";
import CallCenterNav from "../../components/call-center/CallCenterNav";

const campaignSchema = Yup.object({
  name: Yup.string().trim().required("Campaign name is required"),
  callType: Yup.string().oneOf(Object.values(CallType)).required("Call type is required"),
  scheduleCron: Yup.string().trim().required("Schedule cron is required"),
  active: Yup.boolean().required(),
  limit: Yup.number().nullable().transform((v, o) => (o === "" ? undefined : v)).min(1),
});

const initialValues = {
  name: "",
  callType: CallType.MANUAL,
  active: true,
  scheduleCron: "0 */10 * * * *",
  logic: "AND",
  limit: "",
  retryMaxRetries: "",
  retryDelayMinutes: "",
};

const CallCenterCampaignsPage = () => {
  const campaignsApi = useCampaigns();
  const [rows, setRows] = useState([createEmptyConditionRow()]);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const addRow = () => setRows((prev) => [...prev, createEmptyConditionRow()]);
  const removeRow = (index) => setRows((prev) => prev.filter((_, idx) => idx !== index));
  const updateRow = (index, patch) =>
    setRows((prev) => prev.map((row, idx) => (idx === index ? { ...row, ...patch } : row)));

  const triggerPreview = useMemo(
    () => serializeTriggerCondition({ logic: "AND", rows }),
    [rows]
  );

  const handleCreate = async (values, { resetForm }) => {
    try {
      const payload = {
        name: values.name,
        callType: values.callType,
        active: Boolean(values.active),
        scheduleCron: values.scheduleCron,
        triggerCondition: serializeTriggerCondition({
          logic: values.logic,
          limit: values.limit,
          rows,
        }),
        retryPolicy: {
          maxRetries: values.retryMaxRetries === "" ? undefined : Number(values.retryMaxRetries),
          retryDelayMinutes: values.retryDelayMinutes === "" ? undefined : Number(values.retryDelayMinutes),
        },
      };
      await campaignsApi.create(payload);
      toast.success("Campaign launched successfully");
      resetForm();
      setRows([createEmptyConditionRow()]);
      loadCampaigns();
    } catch (error) {
      toast.error(error.message || "Failed to create campaign");
    }
  };

  const loadCampaigns = async () => {
    try {
      const data = await campaignsApi.list();
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error("Failed to load campaigns list");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        <header className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800">Campaign Manager</h2>
          <p className="text-gray-500">Automate and monitor outbound call flows</p>
          <div className="mt-4">
            <CallCenterNav />
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Campaign Setup Form */}
          <div className="lg:col-span-2">
            <Formik initialValues={initialValues} validationSchema={campaignSchema} onSubmit={handleCreate}>
              {({ errors, touched, values }) => (
                <Form className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                    <h3 className="font-bold text-gray-700">Create New Campaign</h3>
                  </div>
                  
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-1">
                        <label className="text-xs font-bold text-gray-500 uppercase">Campaign Name</label>
                        <Field name="name" placeholder="E.g. Q4 Debt Recovery" className="w-full p-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-bold text-gray-500 uppercase">Execution Schedule (Cron)</label>
                        <Field name="scheduleCron" className="w-full font-mono text-sm p-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-bold text-gray-500 uppercase">Call Type</label>
                        <Field as="select" name="callType" className="w-full p-2.5 bg-white border border-gray-200 rounded-lg outline-none">
                          <option value={CallType.MANUAL}>Manual Agent</option>
                          <option value={CallType.AI_AUTOMATED}>AI Automated</option>
                        </Field>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-xs font-bold text-gray-500 uppercase">Max Retries</label>
                          <Field name="retryMaxRetries" type="number" className="w-full p-2.5 border border-gray-200 rounded-lg" />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-bold text-gray-500 uppercase">Delay (Min)</label>
                          <Field name="retryDelayMinutes" type="number" className="w-full p-2.5 border border-gray-200 rounded-lg" />
                        </div>
                      </div>
                    </div>

                    <hr className="border-gray-100" />

                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                          Trigger Conditions
                          <span className="px-2 py-0.5 bg-blue-100 text-[#236DB4] text-[10px] rounded uppercase">{values.logic}</span>
                        </h4>
                        <button type="button" onClick={addRow} className="text-xs font-bold text-[#236DB4] hover:text-blue-800 transition-colors">
                          + Add Rule
                        </button>
                      </div>

                      <div className="space-y-3">
                        {rows.map((row, index) => (
                          <div key={index} className="group flex flex-wrap md:flex-nowrap items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-100 transition-all hover:border-blue-200">
                            <select
                              className="text-xs font-bold bg-white border border-gray-200 rounded p-1.5 outline-none"
                              value={row.mode}
                              onChange={(e) => updateRow(index, { mode: e.target.value })}
                            >
                              <option value="object">FIELD</option>
                              <option value="string">EXPR</option>
                            </select>

                            {row.mode === "string" ? (
                              <input
                                className="flex-1 text-sm p-1.5 border border-gray-200 rounded"
                                placeholder="e.g. balance > 500"
                                value={row.expression}
                                onChange={(e) => updateRow(index, { expression: e.target.value })}
                              />
                            ) : (
                              <>
                                <input placeholder="Field Name" className="flex-1 min-w-[100px] text-sm p-1.5 border border-gray-200 rounded" value={row.field} onChange={(e) => updateRow(index, { field: e.target.value })} />
                                <select className="text-xs p-1.5 border border-gray-200 rounded" value={row.operator} onChange={(e) => updateRow(index, { operator: e.target.value })}>
                                  {CONDITION_OPERATORS.map((op) => <option key={op} value={op}>{op}</option>)}
                                </select>
                                <input placeholder="Value" className="flex-1 min-w-[100px] text-sm p-1.5 border border-gray-200 rounded" value={row.value} onChange={(e) => updateRow(index, { value: e.target.value })} disabled={["ISNULL", "ISNOTNULL"].includes(row.operator)} />
                              </>
                            )}
                            
                            <button type="button" onClick={() => removeRow(index)} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="pt-4 flex items-center justify-between">
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <Field type="checkbox" name="active" className="w-5 h-5 rounded border-gray-300 text-[#236DB4] focus:ring-blue-500" />
                        <span className="text-sm font-medium text-gray-700">Set as Active</span>
                      </label>
                      <button
                        type="submit"
                        disabled={campaignsApi.loading}
                        className="bg-[#236DB4] hover:bg-blue-700 text-white font-bold py-2.5 px-8 rounded-lg shadow-lg shadow-blue-200 transition-all disabled:opacity-50"
                      >
                        {campaignsApi.loading ? "Saving..." : "Create Campaign"}
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>

          {/* Sidebar: List & JSON Preview */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-800">Existing Campaigns</h3>
                <button onClick={loadCampaigns} className="text-[#236DB4] hover:rotate-180 transition-transform duration-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                </button>
              </div>
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                {campaigns.map((c) => (
                  <div key={c.id} className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-bold text-gray-700">{c.name}</div>
                      <div className="text-[10px] text-gray-400 uppercase font-bold tracking-tight">{c.callType}</div>
                    </div>
                    <span className={`h-2 w-2 rounded-full ${c.active ? 'bg-emerald-500 animate-pulse' : 'bg-gray-300'}`}></span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-900 rounded-2xl p-6 text-emerald-400 font-mono text-[10px] shadow-xl">
              <div className="flex justify-between items-center mb-2 border-b border-gray-800 pb-2">
                <span className="text-gray-500 uppercase tracking-widest font-bold">Logic Preview</span>
                <span className="text-emerald-600">JSON</span>
              </div>
              <pre className="overflow-x-auto">
                {JSON.stringify(triggerPreview, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallCenterCampaignsPage;